import type { ReactEventHandler } from "react";

import { ImageZoom } from "@/components/ui/shadcn-io/image-zoom";
import { cn } from "@/lib/utils";

export interface ZoomablePhotoProps {
  url: string;
  alt?: string;
  className?: string;
  parentClassName?: string;
  /**
   * Forwarded to the `<img>`, for a caller that renders something else entirely
   * when the image will not load.
   *
   * Optional and additive: without it a failed load shows the browser's own
   * broken-image icon, which is the right default for a strip of thumbnails.
   * `refresh_review`'s current poster is the case that needs more — a broken
   * icon beside a real candidate reads as "the current poster is broken", a
   * different and more alarming claim than "there is not one" — and before this
   * existed, needing `onError` was the reason that one image was a bare `<img>`
   * and so the only unzoomable half of a side-by-side comparison.
   */
  onError?: ReactEventHandler<HTMLImageElement>;
}

export const ZoomablePhoto = ({
  url,
  alt,
  className,
  parentClassName,
  onError,
}: ZoomablePhotoProps) => {
  return (
    <div
      key={url}
      className={cn("flex flex-row justify-center group", parentClassName)}
    >
      <ImageZoom>
        {/*
          referrerPolicy="no-referrer": these are origin source images. Some
          hosts (e.g. folkandhoney.co.uk behind Cloudflare) hotlink-protect by
          Referer and 403 any cross-origin load, which renders as a broken
          image in IMS. Sending no Referer dodges that — and never hurts, since
          <img> display never needs one.
        */}
        <img
          className={cn(className)}
          src={url}
          alt={alt}
          referrerPolicy="no-referrer"
          onError={onError}
        />
      </ImageZoom>
    </div>
  );
};
